import apiClient from "@/api-client";
import { useQuery } from "@tanstack/react-query";

import { accountsKeys, type Account } from "@/hooks/api/use-accounts";

/**
 * Every account, for the price book's account switcher.
 *
 * `GET /accounts/available` is the wrong list here: it inner-joins `account_users` and so answers
 * only the accounts the caller is a *member* of, which for a platform super admin is usually none.
 * The price book has to be able to read any account's overrides, so it reads the super-admin-only
 * index instead — the same list the Accounts screen is built on.
 *
 * `PaginatedQuery` carries no defaults: without `limit` and `offset` this is a 400, not an
 * unpaginated page. The cap is deliberate rather than incidental, and the screen says so when the
 * list is longer than one page rather than silently hiding accounts.
 *
 * 100 is the ceiling, not a preference — PaginatedQuery.limit carries @Max(100), so anything above
 * it 400s and the switcher disables itself with a message blaming the account list.
 */
export const ACCOUNT_PAGE_SIZE = 100;

export interface PriceBookAccounts {
	accounts: Account[];
	total: number;
	/** True where there are more accounts than this one page, so the switcher can admit it. */
	truncated: boolean;
}

export function usePriceBookAccounts(enabled = true) {
	return useQuery({
		queryKey: accountsKeys.list(`price-book:${ACCOUNT_PAGE_SIZE}`),
		queryFn: async (): Promise<PriceBookAccounts> => {
			const response = await apiClient.get(`/accounts?limit=${ACCOUNT_PAGE_SIZE}&offset=0`);
			const accounts: Account[] = response.data?.data ?? [];
			const total: number = response.data?.total ?? accounts.length;

			return { accounts, total, truncated: total > accounts.length };
		},
		enabled,
		// A 403 here means the same thing a 403 on the price book means, and retrying will not
		// change the answer.
		retry: false,
	});
}
